/**
 * Utility functions for generating breadcrumb navigation items
 * Uses slugService for consistent URL generation
 */
import slugService from '../services/slugService';

/**
 * Creates a Home breadcrumb item
 * @returns {Object} Home breadcrumb item
 */
export const getHomeBreadcrumb = () => ({
  label: 'Home',
  path: '/'
});

/**
 * Creates a Trading Card Sets breadcrumb item
 * @returns {Object} Trading Card Sets breadcrumb item
 */
export const getTradingCardSetsBreadcrumb = () => ({
  label: 'Trading Card Sets',
  path: '/trading-card-sets'
});

/**
 * Creates a Product breadcrumb item
 * @param {Object} product - Product object
 * @returns {Object} Product breadcrumb item
 */
export const getProductBreadcrumb = (product) => {
  if (!product || !product.id) return null;
  
  return {
    label: `${product.name} (${product.year})`,
    path: slugService.getTradingCardSetUrl(product)
  };
};

/**
 * Creates an Insert Set breadcrumb item
 * @param {Object} insertSet - Insert Set object
 * @param {Object} product - Product object (parent of the insert set)
 * @returns {Object} Insert Set breadcrumb item
 */
export const getInsertSetBreadcrumb = (insertSet, product) => {
  if (!insertSet || !insertSet.id || !product || !product.id) return null;
  
  return {
    label: insertSet.name,
    path: slugService.getInsertSetUrl(insertSet, product)
  };
};

/**
 * Creates a Card breadcrumb item
 * @param {Object} card - Card object
 * @param {Object} insertSet - Insert Set object (parent of the card)
 * @param {Object} product - Product object (parent of the insert set)
 * @returns {Object} Card breadcrumb item
 */
export const getCardBreadcrumb = (card, insertSet, product) => {
  if (!card || !card.id) return null;
  
  let path = null;
  
  if (insertSet && product) {
    path = slugService.getCardUrl(card, insertSet, product);
  }
  
  return {
    label: card.name || `Card #${card.cardNumber || card.id}`,
    path: path
  };
};

/**
 * Generates complete breadcrumb items for a product page
 * @param {Object} product - Product object
 * @returns {Array} Array of breadcrumb items
 */
export const getProductBreadcrumbs = (product) => {
  if (!product) return [getHomeBreadcrumb()];
  
  return [
    getHomeBreadcrumb(),
    getTradingCardSetsBreadcrumb(),
    getProductBreadcrumb(product)
  ].filter(Boolean); // Remove any null/undefined items
};

/**
 * Generates complete breadcrumb items for an insert set page
 * @param {Object} insertSet - Insert Set object
 * @param {Object} product - Product object (parent of the insert set)
 * @returns {Array} Array of breadcrumb items
 */
export const getInsertSetBreadcrumbs = (insertSet, product) => {
  if (!insertSet || !product) return [getHomeBreadcrumb()];
  
  return [
    getHomeBreadcrumb(),
    getTradingCardSetsBreadcrumb(),
    getProductBreadcrumb(product),
    getInsertSetBreadcrumb(insertSet, product)
  ].filter(Boolean); // Remove any null/undefined items
};

/**
 * Generates complete breadcrumb items for a card page
 * @param {Object} card - Card object
 * @param {Object} insertSet - Insert Set object (parent of the card)
 * @param {Object} product - Product object (parent of the insert set)
 * @returns {Array} Array of breadcrumb items
 */
export const getCardBreadcrumbs = (card, insertSet, product) => {
  if (!card || !insertSet || !product) return [getHomeBreadcrumb()];
  
  return [
    getHomeBreadcrumb(),
    getTradingCardSetsBreadcrumb(),
    getProductBreadcrumb(product),
    getInsertSetBreadcrumb(insertSet, product),
    getCardBreadcrumb(card, insertSet, product)
  ].filter(Boolean); // Remove any null/undefined items
};

/**
 * Creates a User Profile breadcrumb item
 * @param {Object} user - User object
 * @returns {Object} User Profile breadcrumb item
 */
export const getUserProfileBreadcrumb = (user) => {
  if (!user || !user.username) return null;
  
  return {
    label: user.name || user.username,
    path: `/@${user.username}`
  };
};

/**
 * Creates an Edit Profile breadcrumb item
 * @returns {Object} Edit Profile breadcrumb item
 */
export const getEditProfileBreadcrumb = () => ({
  label: 'Edit Profile',
  path: '/profile'
});

/**
 * Creates a User Collection breadcrumb item
 * @param {Object} user - User object
 * @returns {Object} User Collection breadcrumb item
 */
export const getUserCollectionBreadcrumb = (user) => {
  if (!user || !user.username) return null;
  
  return {
    label: 'Collection',
    path: `/@${user.username}/collection`
  };
};

/**
 * Creates a User Collection Product breadcrumb item
 * @param {Object} user - User object
 * @param {Object} product - Product object
 * @returns {Object} User Collection Product breadcrumb item
 */
export const getUserCollectionProductBreadcrumb = (user, product) => {
  if (!user || !user.username || !product || !product.id) return null;
  
  return {
    label: `${product.name} (${product.year})`,
    path: slugService.getUserCollectionProductUrl(product, user.username)
  };
};

/**
 * Creates a User Collection Insert Set breadcrumb item
 * @param {Object} user - User object
 * @param {Object} insertSet - Insert Set object
 * @param {Object} product - Product object (parent of insert set)
 * @returns {Object} User Collection Insert Set breadcrumb item
 */
export const getUserCollectionInsertSetBreadcrumb = (user, insertSet, product) => {
  if (!user || !user.username || !insertSet || !insertSet.id || !product) return null;
  
  return {
    label: insertSet.name,
    path: slugService.getUserCollectionInsertSetUrl(insertSet, product, user.username)
  };
};

/**
 * Creates a User Collection Card breadcrumb item
 * @param {Object} user - User object
 * @param {Object} card - Card object
 * @param {Object} insertSet - Insert Set object (parent of the card)
 * @param {Object} product - Product object (parent of the insert set)
 * @returns {Object} User Collection Card breadcrumb item
 */
export const getUserCollectionCardBreadcrumb = (user, card, insertSet, product) => {
  if (!user || !user.username || !card || !card.id || !insertSet || !product) return null;
  
  return {
    label: card.name || `Card #${card.cardNumber || card.id}`,
    path: slugService.getUserCollectionCardUrl(card, insertSet, product, user.username)
  };
};

/**
 * Creates a User Wishlist breadcrumb item
 * @param {Object} user - User object
 * @returns {Object} User Wishlist breadcrumb item
 */
export const getUserWishlistBreadcrumb = (user) => {
  if (!user || !user.username) return null;
  
  return {
    label: 'Wishlist',
    path: `/@${user.username}/wishlist`
  };
};

/**
 * Creates a User Wishlist Product breadcrumb item
 * @param {Object} user - User object
 * @param {Object} product - Product object
 * @returns {Object} User Wishlist Product breadcrumb item
 */
export const getUserWishlistProductBreadcrumb = (user, product) => {
  if (!user || !user.username || !product || !product.id) return null;
  
  return {
    label: `${product.name} (${product.year})`,
    path: slugService.getUserWishlistProductUrl(product, user.username)
  };
};

/**
 * Creates a User Wishlist Insert Set breadcrumb item
 * @param {Object} user - User object
 * @param {Object} insertSet - Insert Set object
 * @param {Object} product - Product object (parent of the insert set)
 * @returns {Object} User Wishlist Insert Set breadcrumb item
 */
export const getUserWishlistInsertSetBreadcrumb = (user, insertSet, product) => {
  if (!user || !user.username || !insertSet || !insertSet.id || !product) return null;
  
  return {
    label: insertSet.name,
    path: slugService.getUserWishlistInsertSetUrl(insertSet, product, user.username)
  };
};

/**
 * Creates a User Wishlist Card breadcrumb item
 * @param {Object} user - User object
 * @param {Object} card - Card object
 * @param {Object} insertSet - Insert Set object (parent of the card)
 * @param {Object} product - Product object (parent of the insert set)
 * @returns {Object} User Wishlist Card breadcrumb item
 */
export const getUserWishlistCardBreadcrumb = (user, card, insertSet, product) => {
  if (!user || !user.username || !card || !card.id || !insertSet || !product) return null;
  
  return {
    label: card.name || `Card #${card.cardNumber || card.id}`,
    path: slugService.getUserWishlistCardUrl(card, insertSet, product, user.username)
  };
};

/**
 * Creates a User Cards For Trade breadcrumb item
 * @param {Object} user - User object
 * @returns {Object} User Cards For Trade breadcrumb item
 */
export const getUserAvailableCardsBreadcrumb = (user) => {
  if (!user || !user.username) return null;
  
  return {
    label: 'Available Cards',
    path: `/@${user.username}/available-cards`
  };
};

/**
 * Generates breadcrumb items for a trading card sets page
 * @returns {Array} Array of breadcrumb items
 */
export const getTradingCardSetsBreadcrumbs = () => {
  return [
    getHomeBreadcrumb(),
    getTradingCardSetsBreadcrumb()
  ];
};

/**
 * Generates breadcrumb items for a user profile page
 * @param {Object} user - User object
 * @returns {Array} Array of breadcrumb items
 */
export const getUserProfileBreadcrumbs = (user) => {
  if (!user) return [getHomeBreadcrumb()];
  
  return [
    getHomeBreadcrumb(),
    getUserProfileBreadcrumb(user)
  ].filter(Boolean);
};

/**
 * Generates breadcrumb items for the edit profile page
 * @returns {Array} Array of breadcrumb items
 */
export const getEditProfileBreadcrumbs = () => {
  return [
    getHomeBreadcrumb(),
    getEditProfileBreadcrumb()
  ];
};

/**
 * Generates breadcrumb items for a user collection page
 * @param {Object} user - User object
 * @returns {Array} Array of breadcrumb items
 */
export const getUserCollectionBreadcrumbs = (user) => {
  if (!user) return [getHomeBreadcrumb()];
  
  return [
    getHomeBreadcrumb(),
    getUserProfileBreadcrumb(user),
    getUserCollectionBreadcrumb(user)
  ].filter(Boolean);
};

/**
 * Generates breadcrumb items for a user collection product page
 * @param {Object} user - User object
 * @param {Object} product - Product object
 * @returns {Array} Array of breadcrumb items
 */
export const getUserCollectionProductBreadcrumbs = (user, product) => {
  if (!user || !product) return [getHomeBreadcrumb()];
  
  return [
    getHomeBreadcrumb(),
    getUserProfileBreadcrumb(user),
    getUserCollectionBreadcrumb(user),
    getUserCollectionProductBreadcrumb(user, product)
  ].filter(Boolean);
};

/**
 * Generates breadcrumb items for a user collection insert set page
 * @param {Object} user - User object
 * @param {Object} product - Product object
 * @param {Object} insertSet - Insert Set object
 * @returns {Array} Array of breadcrumb items
 */
export const getUserCollectionInsertSetBreadcrumbs = (user, product, insertSet) => {
  if (!user || !product || !insertSet) return [getHomeBreadcrumb()];
  
  return [
    getHomeBreadcrumb(),
    getUserProfileBreadcrumb(user),
    getUserCollectionBreadcrumb(user),
    getUserCollectionProductBreadcrumb(user, product),
    getUserCollectionInsertSetBreadcrumb(user, insertSet, product)
  ].filter(Boolean);
};

/**
 * Generates breadcrumb items for a user collection card page
 * @param {Object} user - User object
 * @param {Object} product - Product object
 * @param {Object} insertSet - Insert Set object
 * @param {Object} card - Card object
 * @returns {Array} Array of breadcrumb items
 */
export const getUserCollectionCardBreadcrumbs = (user, product, insertSet, card) => {
  if (!user || !product || !insertSet || !card) return [getHomeBreadcrumb()];
  
  return [
    getHomeBreadcrumb(),
    getUserProfileBreadcrumb(user),
    getUserCollectionBreadcrumb(user),
    getUserCollectionProductBreadcrumb(user, product),
    getUserCollectionInsertSetBreadcrumb(user, insertSet, product),
    getUserCollectionCardBreadcrumb(user, card, insertSet, product)
  ].filter(Boolean);
};

/**
 * Generates breadcrumb items for a user wishlist page
 * @param {Object} user - User object
 * @returns {Array} Array of breadcrumb items
 */
export const getUserWishlistBreadcrumbs = (user) => {
  if (!user) return [getHomeBreadcrumb()];
  
  return [
    getHomeBreadcrumb(),
    getUserProfileBreadcrumb(user),
    getUserWishlistBreadcrumb(user)
  ].filter(Boolean);
};

/**
 * Generates breadcrumb items for a user wishlist product page
 * @param {Object} user - User object
 * @param {Object} product - Product object
 * @returns {Array} Array of breadcrumb items
 */
export const getUserWishlistProductBreadcrumbs = (user, product) => {
  if (!user || !product) return [getHomeBreadcrumb()];
  
  return [
    getHomeBreadcrumb(),
    getUserProfileBreadcrumb(user),
    getUserWishlistBreadcrumb(user),
    getUserWishlistProductBreadcrumb(user, product)
  ].filter(Boolean);
};

/**
 * Generates breadcrumb items for a user wishlist insert set page
 * @param {Object} user - User object
 * @param {Object} product - Product object
 * @param {Object} insertSet - Insert Set object
 * @returns {Array} Array of breadcrumb items
 */
export const getUserWishlistInsertSetBreadcrumbs = (user, product, insertSet) => {
  if (!user || !product || !insertSet) return [getHomeBreadcrumb()];
  
  return [
    getHomeBreadcrumb(),
    getUserProfileBreadcrumb(user),
    getUserWishlistBreadcrumb(user),
    getUserWishlistProductBreadcrumb(user, product),
    getUserWishlistInsertSetBreadcrumb(user, insertSet, product)
  ].filter(Boolean);
};

/**
 * Generates breadcrumb items for a user wishlist card page
 * @param {Object} user - User object
 * @param {Object} product - Product object
 * @param {Object} insertSet - Insert Set object
 * @param {Object} card - Card object
 * @returns {Array} Array of breadcrumb items
 */
export const getUserWishlistCardBreadcrumbs = (user, product, insertSet, card) => {
  if (!user || !product || !insertSet || !card) return [getHomeBreadcrumb()];
  
  return [
    getHomeBreadcrumb(),
    getUserProfileBreadcrumb(user),
    getUserWishlistBreadcrumb(user),
    getUserWishlistProductBreadcrumb(user, product),
    getUserWishlistInsertSetBreadcrumb(user, insertSet, product),
    getUserWishlistCardBreadcrumb(user, card, insertSet, product)
  ].filter(Boolean);
};

/**
 * Creates a User Cards For Trade Product breadcrumb item
 * @param {Object} user - User object
 * @param {Object} product - Product object
 * @returns {Object} User Cards For Trade Product breadcrumb item
 */
export const getUserAvailableCardsProductBreadcrumb = (user, product) => {
  if (!user || !user.username || !product || !product.id) return null;
  
  return {
    label: `${product.name} (${product.year})`,
    path: slugService.getUserAvailableCardsProductUrl(product, user.username)
  };
};

/**
 * Creates a User Cards For Trade Insert Set breadcrumb item
 * @param {Object} user - User object
 * @param {Object} insertSet - Insert Set object
 * @param {Object} product - Product object (parent of insert set)
 * @returns {Object} User Cards For Trade Insert Set breadcrumb item
 */
export const getUserAvailableCardsInsertSetBreadcrumb = (user, insertSet, product) => {
  if (!user || !user.username || !insertSet || !insertSet.id || !product) return null;
  
  return {
    label: insertSet.name,
    path: slugService.getUserAvailableCardsInsertSetUrl(insertSet, product, user.username)
  };
};

/**
 * Generates breadcrumb items for a user cards for trade page
 * @param {Object} user - User object
 * @returns {Array} Array of breadcrumb items
 */
export const getUserAvailableCardsBreadcrumbs = (user) => {
  if (!user) return [getHomeBreadcrumb()];
  
  return [
    getHomeBreadcrumb(),
    getUserProfileBreadcrumb(user),
    getUserAvailableCardsBreadcrumb(user)
  ].filter(Boolean);
};

/**
 * Generates breadcrumb items for a user cards for trade product page
 * @param {Object} user - User object
 * @param {Object} product - Product object
 * @returns {Array} Array of breadcrumb items
 */
export const getUserAvailableCardsProductBreadcrumbs = (user, product) => {
  if (!user || !product) return [getHomeBreadcrumb()];
  
  return [
    getHomeBreadcrumb(),
    getUserProfileBreadcrumb(user),
    getUserAvailableCardsBreadcrumb(user),
    getUserAvailableCardsProductBreadcrumb(user, product)
  ].filter(Boolean);
};

/**
 * Generates breadcrumb items for a user cards for trade insert set page
 * @param {Object} user - User object
 * @param {Object} product - Product object
 * @param {Object} insertSet - Insert Set object
 * @returns {Array} Array of breadcrumb items
 */
export const getUserAvailableCardsInsertSetBreadcrumbs = (user, product, insertSet) => {
  if (!user || !product || !insertSet) return [getHomeBreadcrumb()];
  
  return [
    getHomeBreadcrumb(),
    getUserProfileBreadcrumb(user),
    getUserAvailableCardsBreadcrumb(user),
    getUserAvailableCardsProductBreadcrumb(user, product),
    getUserAvailableCardsInsertSetBreadcrumb(user, insertSet, product)
  ].filter(Boolean);
};

/**
 * Generates breadcrumb items for a user cards for trade card page
 * @param {Object} user - User object
 * @param {Object} product - Product object
 * @param {Object} insertSet - Insert Set object
 * @param {Object} card - Card object
 * @returns {Array} Array of breadcrumb items
 */
export const getUserAvailableCardsCardBreadcrumbs = (user, product, insertSet, card) => {
  if (!user || !product || !insertSet || !card) return [getHomeBreadcrumb()];
  
  return [
    getHomeBreadcrumb(),
    getUserProfileBreadcrumb(user),
    getUserAvailableCardsBreadcrumb(user),
    getUserAvailableCardsProductBreadcrumb(user, product),
    getUserAvailableCardsInsertSetBreadcrumb(user, insertSet, product),
    getCardBreadcrumb(card, insertSet, product)
  ].filter(Boolean);
};